/**
 * Frontend version of the buildUrl function from apps/saas_back/src/utils/leadsStats.ts
 * This function follows the same pattern as the backend function but returns a relative URL
 * for frontend navigation instead of an absolute URL with domain.
 */

interface FilterOptions {
  startDate: Date;
  endDate: Date;
  businessUnitId: number;
  sources?: string[];
  brands?: string[];
  origins?: string[];
  subOrigins?: string[];
  branchOffices?: string[];
  visitDate?: boolean;
  CRMUpdateAt?: boolean;
  noCRMUpdateAt?: boolean;
  interestedCRMLeads?: boolean;
  notInterestedCRMLeads?: boolean;
  discarded?: boolean;
  hasClientMessages?: boolean;
  noClientMessages?: boolean;
  isAssignedToExecutive?: boolean;
  leadsFailed?: boolean;
  leadsContacted?: boolean;
  pendingLeads?: boolean;
  outsideWorkingHours?: boolean;
  templatesSentCount?: boolean;
  viewMode?: 'table' | 'kanban';
  campaignIds?: string[];
  /** Kovacs: filter by AI tipificación (AIComment) */
  AIComment?: string;
  /** Filter failed-bucket leads by `failReason`. Pass `'null'` to filter unclassified. */
  failReason?: string;
  /** Financial institutions: bank offer state ever reached, matching the dashboard funnel stages. */
  bankOfferStatus?: string;
}

/**
 * Builds a URL with filters to navigate to the leads page
 * This mirrors the backend buildUrl function logic but returns a relative path
 *
 * @param filters - Filter options for the leads page
 * @returns The relative URL string with encoded filter parameters
 */
const buildLeadsFilterUrl = (filters: FilterOptions): string => {
  // Bar-level filters (createdAt / sources) live in the `leads.*` namespace so
  // the Leads page MasterFilter picks them up via URL persistence. Putting them in
  // `filterModel` instead causes a double-filter: the bar adds its own createdAt on top
  // of the items in advancedFilterModel.
  const items: { field: string; operator: string; value: string }[] = [];

  if (filters.visitDate) {
    items.push({ field: 'visitDate', operator: 'isNotEmpty', value: '' });
  }

  if (filters.CRMUpdateAt) {
    items.push({ field: 'CRMUpdateAt', operator: 'isNotEmpty', value: '' });
  }

  if (filters.noCRMUpdateAt) {
    items.push({ field: 'CRMUpdateAt', operator: 'isEmpty', value: '' });
  }

  if (filters.interestedCRMLeads) {
    items.push({ field: 'CRMUpdateAt', operator: 'isNotEmpty', value: '' });
    // Use `in` (enum codec) so the chip roundtrips through `gridItemsToActiveFilters`.
    items.push({ field: 'crmUploadReason', operator: 'in', value: 'interested' });
  }

  if (filters.notInterestedCRMLeads) {
    // `crmUploadReason` is only ever 'interested' or NULL today, so `isEmpty` correctly
    // captures "uploaded to CRM but not flagged as interested" (NOT-equals would miss NULLs
    // in SQL three-valued logic).
    items.push({ field: 'CRMUpdateAt', operator: 'isNotEmpty', value: '' });
    items.push({ field: 'crmUploadReason', operator: 'isEmpty', value: '' });
  }

  if (filters.discarded) {
    items.push({ field: 'discarded', operator: 'equals', value: 'true' });
  }

  if (filters.hasClientMessages) {
    items.push({ field: 'clientMessagesCount', operator: 'notEquals', value: '0' });
  }

  if (filters.noClientMessages) {
    items.push({ field: 'clientMessagesCount', operator: 'equals', value: '0' });
  }

  // Use strict range operators (`greaterThan` / `lessThan`) instead of `equals`/`notEquals`
  // so the smart-filter modal can roundtrip these items back into editable `numericRange`
  // rows (its codec only reads gt/lt). For integer count columns, `< 1` ≡ `== 0` and
  // `> 0` ≡ `!= 0`.
  if (filters.leadsFailed) {
    items.push({ field: 'messagesCount', operator: 'lessThan', value: '1' });
    items.push({ field: 'failedMessagesCount', operator: 'greaterThan', value: '0' });
    items.push({ field: 'discarded', operator: 'equals', value: 'false' });
  }

  if (filters.leadsContacted) {
    items.push({ field: 'messagesCount', operator: 'greaterThan', value: '0' });
    items.push({ field: 'discarded', operator: 'equals', value: 'false' });
  }

  if (filters.pendingLeads) {
    items.push({ field: 'messagesCount', operator: 'lessThan', value: '1' });
    items.push({ field: 'failedMessagesCount', operator: 'lessThan', value: '1' });
    items.push({ field: 'discarded', operator: 'equals', value: 'false' });
  }

  if (filters.outsideWorkingHours) {
    items.push({ field: 'createdAtOutsideWorkingHours', operator: 'equals', value: 'true' });
    // Off-hours metric excludes discarded leads, so mirror that here for parity with the
    // dashboard card numerator.
    items.push({ field: 'discarded', operator: 'equals', value: 'false' });
  }

  if (filters.templatesSentCount) {
    items.push({ field: 'templatesSentCount', operator: 'greaterThan', value: '0' });
  }

  if (filters.campaignIds && filters.campaignIds.length > 0) {
    items.push({ field: 'whatsAppCampaign', operator: 'in', value: filters.campaignIds.join(',') });
  }

  if (filters.origins && filters.origins.length > 0) {
    items.push({ field: 'origin', operator: 'in', value: filters.origins.join(',') });
  }

  if (filters.subOrigins && filters.subOrigins.length > 0) {
    items.push({ field: 'subOrigin', operator: 'in', value: filters.subOrigins.join(',') });
  }

  if (filters.branchOffices && filters.branchOffices.length > 0) {
    items.push({ field: 'branchOffice', operator: 'in', value: filters.branchOffices.join(',') });
  }

  if (filters.brands && filters.brands.length > 0) {
    // Dashboard "Marca" filters `Lead.initialBrandOfInterest`, not the bar's `currentBrand`
    // (`modelsOfInterest[0].brand`). Carry it as the matching advanced filter so the leads
    // list reproduces the dashboard's count instead of silently dropping the selection.
    items.push({ field: 'initialBrandOfInterest', operator: 'in', value: filters.brands.join(',') });
  }

  if (filters.AIComment != null && filters.AIComment !== '') {
    items.push({ field: 'AIComment', operator: 'equals', value: filters.AIComment });
  }

  if (filters.failReason) {
    // Enum filter rows serialize as `{ operator: 'in', value: 'csv' }` (see smartFilterMapping.ts).
    // 'null' from the metrics breakdown gets remapped to the `__null__` sentinel so the
    // backend `failReason` special-case can OR `IS NULL` into the `IN` clause, and the
    // value roundtrips through the smart-filter modal as a normal enum option.
    const value = filters.failReason === 'null' || filters.failReason === '__null__' ? '__null__' : filters.failReason;
    items.push({ field: 'failReason', operator: 'in', value });
  }

  if (filters.bankOfferStatus) {
    items.push({ field: 'bankOfferStatus', operator: 'in', value: filters.bankOfferStatus });
  }

  items.push({ field: 'isTestLead', operator: 'equals', value: 'false' });

  const paginationModel = { page: 0, pageSize: 100 };
  const params = new URLSearchParams();
  params.set('businessUnitId', filters.businessUnitId.toString());
  params.set('paginationModel', JSON.stringify(paginationModel));

  // Bar filters via MasterFilter persistence (matches `urlPersistence.ts` codecs).
  params.set('leads.dateRange.start', filters.startDate.toISOString());
  params.set('leads.dateRange.end', filters.endDate.toISOString());
  if (filters.sources && filters.sources.length > 0) {
    params.set('leads.sources', JSON.stringify(filters.sources));
  }

  // Advanced filter items go to `leads.advancedFilterModel` — Leads page reads this key
  // first and falls back to `filterModel` only when absent. Writing to `filterModel`
  // alone would be ignored when the user has prior namespaced advanced state in the URL.
  if (items.length > 0) {
    params.set('leads.advancedFilterModel', JSON.stringify({ items }));
  }

  if (filters.viewMode) {
    params.set('viewMode', filters.viewMode);
  }

  return `/leads?${params.toString()}`;
};

export default buildLeadsFilterUrl;
